import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import { FileText, Loader2 } from 'lucide-react'
import { ClientRequest } from '../../../requests/ClientRequest'
import { Timezones } from '../../../common/Timezones'
import { WorkRegion } from '../../../model/WorkRegion'

interface ClientOverviewSectionProps {
  formData: ClientRequest
  onChange: (field: keyof ClientRequest, value: any) => void
  onSave: () => void
  errors: Record<string, string>
  isSaving: boolean
  isAdmin: boolean
  regions: WorkRegion[]
}

export const ClientOverviewSection: React.FC<ClientOverviewSectionProps> = ({
  formData,
  onChange,
  onSave,
  errors,
  isSaving,
  isAdmin,
  regions,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Client Overview
        </CardTitle>
        <CardDescription>Basic information and settings for this client</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => onChange('name', e.target.value)}
              disabled={!isAdmin || isSaving}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="workRegion">Work Region *</Label>
            <Select
              value={formData.workRegionId === -1 ? '' : formData.workRegionId.toString()}
              onValueChange={(value) => onChange('workRegionId', parseInt(value, 10))}
              disabled={!isAdmin || isSaving}
            >
              <SelectTrigger className={errors.workRegionId ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select a work region" />
              </SelectTrigger>
              <SelectContent>
                {regions.map((region) => (
                  <SelectItem key={region.id} value={region.id.toString()}>
                    {region.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.workRegionId && (
              <p className="text-sm text-red-500">{errors.workRegionId}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Select
              value={formData.timezone}
              onValueChange={(value) => onChange('timezone', value)}
              disabled={!isAdmin || isSaving}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Timezones.map((t) => (
                  <SelectItem key={t.code} value={t.code}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notifyOnNewRole">New Role Notification Email(s)</Label>
            <Input
              id="notifyOnNewRole"
              value={formData.notifyOnNewRole}
              onChange={(e) => onChange('notifyOnNewRole', e.target.value)}
              disabled={!isAdmin || isSaving}
              placeholder="email1@example.com, email2@example.com"
            />
            <p className="text-sm text-muted-foreground">
              Comma-separated emails that receive notifications when new roles are created.
            </p>
          </div>
        </div>

        {isAdmin && (
          <div className="border-t pt-4 flex justify-end">
            <Button onClick={onSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
